import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/base/MovieCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import ShareModal from "@/components/base/ShareModal";
import EmptyState from "@/components/base/EmptyState";
import { getMovieById, getRelatedMovies } from "@/lib/catalog";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const movie = getMovieById(id);
  const [shareOpen, setShareOpen] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();

  useSeo({
    title: movie ? `${movie.title} - 影视详情 - 姐姐的朋友` : "影视详情 - 姐姐的朋友",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}${movie.type}）剧情简介与主演信息：${movie.summary} 导演 ${movie.director}，主演 ${movie.cast.join("、")}。`
      : "查看影视作品详情、剧情简介与主演信息。",
    keywords: movie
      ? `${movie.title},${movie.type},${movie.genres.join(",")},剧情介绍,主演,姐姐的朋友`
      : "影视详情,剧情介绍,姐姐的朋友",
  });

  if (!movie) {
    return (
      <div className="flex min-h-screen flex-col bg-background-100">
        <SiteHeader />
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-16 md:px-6">
          <EmptyState
            title="抱歉，未找到该影视内容"
            description="这部作品可能已下架或链接有误，你可以回到分类页继续浏览其他影视剧。"
            actionLabel="返回影视分类"
            onAction={() => navigate("/category")}
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedMovies(movie, 4);
  const favorited = isFavorite(movie.id);

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main>
        <section className="relative overflow-hidden bg-primary-950">
          <div className="absolute inset-0">
            <img
              src={movie.still ?? movie.poster}
              alt={`${movie.title} 背景图`}
              className="h-full w-full scale-110 object-cover object-center opacity-30 blur-sm"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/60" />
          </div>

          <div className="relative mx-auto w-full max-w-[1400px] px-4 pb-8 pt-5 md:px-6 md:pb-10 md:pt-6">
            <div className="[&_a]:text-primary-300 [&_a:hover]:text-accent-400 [&_span]:text-primary-400">
              <Breadcrumb
                items={[
                  { label: "首页", to: "/" },
                  { label: "影视分类", to: "/category" },
                  { label: movie.type, to: `/category?type=${encodeURIComponent(movie.type)}` },
                  { label: movie.title },
                ]}
              />
            </div>

            <div className="mt-5 flex flex-col gap-6 md:flex-row md:gap-9">
              <div className="w-full max-w-[190px] shrink-0 sm:max-w-[220px] md:w-[250px] md:max-w-none">
                <div className="aspect-[3/4] w-full overflow-hidden rounded-lg bg-primary-900">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} 影视封面`}
                    title={`${movie.title} - ${movie.genres.join("、")} - 姐姐的朋友`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-md bg-accent-500 px-2 py-1 font-medium text-primary-950">{movie.type}</span>
                  <span className="rounded-md border border-primary-700 px-2 py-1 text-primary-200">{movie.region}</span>
                  {movie.tags.map((tag) => (
                    <span key={tag} className="rounded-md bg-primary-800/70 px-2 py-1 text-primary-200">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="mt-3 text-2xl font-black text-background-50 md:text-4xl">{movie.title}</h1>
                <p className="mt-1 font-label text-xs tracking-[0.14em] text-primary-300">
                  {movie.originalTitle.toUpperCase()}
                </p>

                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-primary-100">
                  <span>{movie.year}</span>
                  <span className="text-primary-500">|</span>
                  <span>{movie.genres.join(" / ")}</span>
                  <span className="text-primary-500">|</span>
                  <span>{movie.duration}</span>
                  <span className="text-primary-500">|</span>
                  <span>更新于 {movie.updatedAt}</span>
                </div>

                <p className="mt-4 max-w-2xl border-l-2 border-accent-500 pl-3 text-sm leading-7 text-background-100/90">
                  {movie.tagline}
                </p>

                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-label text-3xl font-extrabold text-accent-400">{movie.rating.toFixed(1)}</span>
                  <span className="text-xs text-primary-300">/ 10 · 站内评分</span>
                  <span className="ml-2 text-xs text-primary-300">热度 {movie.popularity}</span>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => toggleFavorite(movie.id)}
                    aria-pressed={favorited}
                    className={[
                      "inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors",
                      favorited
                        ? "bg-background-50 text-primary-800"
                        : "bg-accent-500 text-primary-950 hover:bg-accent-400",
                    ].join(" ")}
                  >
                    <i className={favorited ? "ri-bookmark-fill text-base" : "ri-bookmark-line text-base"} />
                    {favorited ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShareOpen(true)}
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-4 py-2.5 text-sm text-background-50 transition-colors hover:border-accent-400 hover:text-accent-400"
                  >
                    <i className="ri-share-forward-line text-base" />
                    分享
                  </button>
                  <Link
                    to={`/category?type=${encodeURIComponent(movie.type)}`}
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/25 px-4 py-2.5 text-sm text-primary-100 transition-colors hover:border-primary-400"
                  >
                    <i className="ri-folder-line text-base" />
                    所属分类：{movie.type}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-11">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-8">
              <section>
                <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
                  <span className="h-5 w-1 rounded-full bg-accent-500" />
                  剧情简介
                </h2>
                <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <p className="text-sm font-medium leading-7 text-foreground-900">{movie.summary}</p>
                  <div className="mt-4 space-y-4 border-t border-background-200 pt-4">
                    {movie.synopsis.map((paragraph, index) => (
                      <p key={index} className="text-sm leading-8 text-foreground-600">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </section>

              <section className="mt-8">
                <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
                  <span className="h-5 w-1 rounded-full bg-primary-500" />
                  演职员
                </h2>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <p className="text-xs text-foreground-500">导演</p>
                    <p className="mt-2 text-sm font-semibold text-foreground-900">{movie.director}</p>
                  </div>
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <p className="text-xs text-foreground-500">主演</p>
                    <p className="mt-2 text-sm font-semibold leading-6 text-foreground-900">{movie.cast.join(" / ")}</p>
                  </div>
                </div>
              </section>

              <section className="mt-8 flex flex-wrap items-center gap-2.5 rounded-lg bg-secondary-100 px-5 py-4">
                <span className="text-xs text-secondary-800">浏览更多：</span>
                {[movie.type, movie.region, ...movie.genres].map((tag) => (
                  <Link
                    key={tag}
                    to={
                      tag === movie.type
                        ? `/category?type=${encodeURIComponent(tag)}`
                        : tag === movie.region
                          ? `/category?region=${encodeURIComponent(tag)}`
                          : `/category?q=${encodeURIComponent(tag)}`
                    }
                    className="cursor-pointer rounded-full bg-background-50 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-accent-100 hover:text-accent-900"
                  >
                    {tag}
                  </Link>
                ))}
              </section>
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <div className="rounded-lg border border-background-200 bg-background-50">
                  <h2 className="border-b border-background-200 px-5 py-4 text-base font-bold text-foreground-950">
                    基础信息
                  </h2>
                  <dl className="divide-y divide-background-200 px-5 text-sm">
                    {[
                      { label: "类型", value: movie.type },
                      { label: "地区", value: movie.region },
                      { label: "年份", value: String(movie.year) },
                      { label: "时长", value: movie.duration },
                      { label: "题材", value: movie.genres.join(" / ") },
                      { label: "导演", value: movie.director },
                    ].map((row) => (
                      <div key={row.label} className="flex gap-4 py-3">
                        <dt className="w-16 shrink-0 text-xs text-foreground-500">{row.label}</dt>
                        <dd className="flex-1 text-xs leading-6 text-foreground-800">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="border-t border-background-200 p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">
                          {favorited ? "已加入我的收藏" : "收藏这部作品"}
                        </p>
                        <p className="mt-1 text-[11px] text-foreground-500">收藏保存在本机浏览器，可随时取消</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleFavorite(movie.id)}
                        aria-label={favorited ? "取消收藏" : "收藏"}
                        className={[
                          "grid h-10 w-10 shrink-0 cursor-pointer place-items-center rounded-md transition-colors",
                          favorited
                            ? "bg-accent-500 text-primary-950"
                            : "bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-900",
                        ].join(" ")}
                      >
                        <i className={favorited ? "ri-bookmark-fill text-lg" : "ri-bookmark-line text-lg"} />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShareOpen(true)}
                      className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                    >
                      <i className="ri-share-forward-line text-sm" />
                      分享给朋友
                    </button>
                  </div>
                </div>

                <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                  <div className="flex items-center justify-between">
                    <h2 className="text-base font-bold text-foreground-950">相关推荐</h2>
                    <span className="font-label text-[11px] text-foreground-400">RELATED</span>
                  </div>
                  <div className="mt-2">
                    {related.map((item) => (
                      <Link
                        key={item.id}
                        to={`/detail/${item.id}`}
                        className="group flex cursor-pointer gap-3 border-b border-background-200 py-3 last:border-b-0"
                      >
                        <div className="h-20 w-14 shrink-0 overflow-hidden rounded bg-background-200">
                          <img
                            src={item.poster}
                            alt={`${item.title} 封面`}
                            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="line-clamp-1 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </p>
                          <p className="mt-1 line-clamp-1 text-[11px] text-foreground-500">
                            {item.year} · {item.genres.slice(0, 2).join(" / ")}
                          </p>
                          <p className="mt-1 line-clamp-2 text-[11px] leading-5 text-foreground-500">{item.summary}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>

          <section className="mt-10 md:mt-14">
            <div className="flex items-end justify-between gap-3">
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-primary-500" />
                你可能也喜欢
              </h2>
              <Link
                to={`/category?q=${encodeURIComponent(movie.genres[0])}`}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
              >
                更多{movie.genres[0]}
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {related.map((item) => (
                <MovieCard key={`grid-${item.id}`} movie={item} compact />
              ))}
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`${movie.title} - 姐姐的朋友`}
        linkText="复制链接"
      />
    </div>
  );
}